import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import {
  assignAdminOrderManager,
  claimAdminOrder,
  purgeAdminOrder,
  type AdminManagerBrief,
} from '../../api';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { useInvalidateCrm } from '../crm/useCrmQueries';

export type AdminOrderActionTone = 'success' | 'error';

export type AdminOrderActionMessage = {
  orderId: number;
  text: string;
  tone: AdminOrderActionTone;
};

type Options<T extends { id: number; order_number: string; status: string; assigned_manager_id?: number | null; assigned_manager?: { id: number; first_name?: string; username?: string } | null }> = {
  userId: number | null;
  firstName: string | null;
  managers: AdminManagerBrief[];
  setOrders: Dispatch<SetStateAction<T[]>>;
  updating: number | null;
  setUpdating: Dispatch<SetStateAction<number | null>>;
  refreshOrders: (opts?: { soft?: boolean }) => Promise<void>;
  refreshCrmStats?: () => Promise<void>;
  onClaimSuccess?: (orderId: number) => void;
  hapticSuccess: () => void;
  hapticSelection: () => void;
};

export function useAdminOrderActions<
  T extends {
    id: number;
    order_number: string;
    status: string;
    assigned_manager_id?: number | null;
    assigned_manager?: { id: number; first_name?: string; username?: string } | null;
  },
>({
  userId,
  firstName,
  managers,
  setOrders,
  updating: _updating,
  setUpdating,
  refreshOrders,
  refreshCrmStats,
  onClaimSuccess,
  hapticSuccess,
  hapticSelection,
}: Options<T>) {
  const invalidateCrm = useInvalidateCrm();
  const [actionMessage, setActionMessage] = useState<AdminOrderActionMessage | null>(null);

  const syncAfterMutation = useCallback(async () => {
    void invalidateCrm();
    await Promise.all([refreshOrders({ soft: true }), refreshCrmStats?.() ?? Promise.resolve()]);
  }, [invalidateCrm, refreshOrders, refreshCrmStats]);

  const claimOrder = useCallback(
    async (orderId: number) => {
      if (!userId) {
        setActionMessage({
          orderId,
          text: 'Нет user_id в сессии — перезайдите в Mini App',
          tone: 'error',
        });
        return;
      }
      setUpdating(orderId);
      setActionMessage(null);
      try {
        const res = (await claimAdminOrder(orderId)) as {
          assigned_manager_id?: number;
          status?: string;
        };
        const mgrId = res.assigned_manager_id ?? userId;
        const meBrief =
          managers.find((m) => m.id === mgrId) ??
          ({ id: mgrId, first_name: firstName ?? undefined, username: undefined } as AdminManagerBrief);
        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId
              ? {
                  ...o,
                  assigned_manager_id: mgrId,
                  assigned_manager: {
                    id: meBrief.id,
                    first_name: meBrief.first_name ?? undefined,
                    username: meBrief.username ?? undefined,
                  },
                  status: res.status ?? o.status,
                }
              : o,
          ),
        );
        await syncAfterMutation();
        onClaimSuccess?.(orderId);
        setActionMessage({
          orderId,
          text: 'Заказ взят в работу · смотрите во Воронке',
          tone: 'success',
        });
        hapticSuccess();
      } catch (err: unknown) {
        setActionMessage({
          orderId,
          text: formatAdminPanelError(err, 'Не удалось взять заказ'),
          tone: 'error',
        });
      } finally {
        setUpdating(null);
      }
    },
    [
      userId,
      firstName,
      managers,
      setOrders,
      setUpdating,
      syncAfterMutation,
      onClaimSuccess,
      hapticSuccess,
    ],
  );

  const assignOrderManager = useCallback(
    async (orderId: number, managerId: number | null) => {
      setUpdating(orderId);
      try {
        await assignAdminOrderManager(orderId, managerId);
        await syncAfterMutation();
        setActionMessage({
          orderId,
          text: managerId ? 'Менеджер назначен' : 'Менеджер снят с заказа',
          tone: 'success',
        });
        hapticSuccess();
      } catch (err: unknown) {
        setActionMessage({
          orderId,
          text: formatAdminPanelError(err, 'Не удалось назначить менеджера'),
          tone: 'error',
        });
      } finally {
        setUpdating(null);
      }
    },
    [setUpdating, syncAfterMutation, hapticSuccess],
  );

  const purgeOrderById = useCallback(
    async (order: T) => {
      hapticSelection();
      setUpdating(order.id);
      try {
        await purgeAdminOrder(order.id);
        hapticSuccess();
        setActionMessage(null);
        await syncAfterMutation();
        return true;
      } catch (err: unknown) {
        setActionMessage({
          orderId: order.id,
          text: formatAdminPanelError(err, 'Не удалось удалить заказ'),
          tone: 'error',
        });
        return false;
      } finally {
        setUpdating(null);
      }
    },
    [hapticSelection, hapticSuccess, setUpdating, syncAfterMutation],
  );

  return {
    actionMessage,
    setActionMessage,
    claimOrder,
    assignOrderManager,
    purgeOrderById,
    syncAfterMutation,
  };
}
